import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3,
  ChevronRight,
  CircleDollarSign,
  Compass,
  ExternalLink,
  FileText,
  Gauge,
  Home,
  KeyRound,
  Layers3,
  Link2,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Brand, buttonClass } from "./ui";
import { classNames, initials } from "../lib/format";
import { api, isDemoMode } from "../lib/api";
import { queryKeys } from "../lib/query";
import { CookieConsent } from "./cookie-consent";

function DemoModeBanner() {
  return isDemoMode ? <div className="flex min-h-8 items-center justify-center gap-1.5 border-b border-[#ebcf77] bg-[#fff1c8] px-4 py-1.5 text-center text-[13px] font-extrabold text-[#6f4d00]" role="status"><Sparkles size={14} /> Modo demonstração — os dados exibidos são exemplos.</div> : null;
}

const shellClass = "mx-auto w-[min(1180px,calc(100%_-_32px))]";
const skipLinkClass = "fixed -top-20 left-4 z-[1000] rounded-b-lg bg-lime px-4 py-2.5 font-extrabold text-ink transition-[top] duration-200 focus:top-0";
const accountItemClass = "flex min-h-[38px] items-center gap-[9px] rounded-[9px] border-0 bg-transparent px-[9px] text-left text-xs font-[750] text-ink transition-[background,color,transform] duration-150 hover:translate-x-px hover:bg-[rgba(102,0,153,.075)] hover:text-forest";
const mobileNavItemClass = "flex min-h-12 items-center justify-between border-0 border-b border-line bg-transparent px-1 py-2.5 text-left font-bold text-ink";

type AccountMenuProps = {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  onSignOut: () => Promise<void>;
};

function AccountMenu({ user, onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeFromOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeFromKeyboard = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeFromOutside);
    document.addEventListener("keydown", closeFromKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutside);
      document.removeEventListener("keydown", closeFromKeyboard);
    };
  }, [open]);

  const close = () => setOpen(false);
  const firstName = user.name.trim().split(/\s+/)[0] || "Conta";
  return (
    <div className="relative flex items-center" ref={rootRef}>
      <button ref={triggerRef} className="group flex min-h-12 items-center gap-2.5 border-0 bg-transparent p-0 text-ink transition-[transform,color] duration-200 hover:-translate-y-px hover:text-purple aria-expanded:-translate-y-px aria-expanded:text-purple" type="button" aria-label="Abrir menu da conta" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-extrabold">{firstName}</span>
        <span className="relative grid size-[42px] shrink-0 place-items-center rounded-full bg-[linear-gradient(145deg,#a33bd0_0%,#660099_58%,#410064_100%)] shadow-[0_5px_14px_rgba(102,0,153,.25)] transition-[transform,box-shadow] duration-200 group-hover:scale-[1.04] group-hover:shadow-[0_7px_18px_rgba(102,0,153,.32)] group-aria-expanded:scale-[1.04] group-aria-expanded:shadow-[0_7px_18px_rgba(102,0,153,.32)]">
          {user.photoUrl
            ? <img className="size-[42px] rounded-full object-cover object-center" src={user.photoUrl} alt="" />
            : <span className="grid size-[42px] place-items-center rounded-full text-xs leading-none font-black tracking-[.02em] text-white">{initials(user.name || "Usuário")}</span>}
          <span className="absolute right-[-1px] bottom-0 size-2.5 rounded-full border-2 border-white bg-[#8d22bd] shadow-[0_1px_4px_rgba(42,20,49,.25)]" aria-hidden="true" />
        </span>
      </button>
      {open && <div className="absolute top-[calc(100%+10px)] right-0 z-[120] grid w-[214px] origin-top-right animate-[account-menu-in_.16s_ease-out] rounded-[15px] border border-[rgba(72,45,80,.16)] bg-[rgba(253,251,254,.98)] p-[7px] shadow-[0_20px_48px_rgba(35,18,40,.18),0_3px_10px_rgba(35,18,40,.06)] backdrop-blur-[18px] before:absolute before:-top-[5px] before:right-[25px] before:size-[9px] before:rotate-45 before:border-t before:border-l before:border-[rgba(72,45,80,.16)] before:bg-surface-raised" role="menu">
        <div className="mb-1 grid min-w-0 gap-px border-b border-line px-[9px] pt-[9px] pb-[11px]"><strong className="overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-[1.4]">{user.name}</strong><small className="overflow-hidden text-ellipsis whitespace-nowrap text-[10px] leading-[1.4] text-muted">{user.email}</small></div>
        <Link className={`${accountItemClass} [&_svg]:text-purple`} role="menuitem" to="/dashboard" onClick={close}><Gauge size={17} /> Meu painel</Link>
        {user.role === "ADMIN" && <Link className={`${accountItemClass} [&_svg]:text-purple`} role="menuitem" to="/admin" onClick={close}><ShieldCheck size={17} /> Painel administrativo</Link>}
        <button className={`${accountItemClass} mt-1 rounded-t-none border-t border-line text-danger`} role="menuitem" type="button" onClick={() => { close(); void onSignOut(); }}><LogOut size={17} /> Sair</button>
      </div>}
    </div>
  );
}

const publicLinks = [
  { to: "/buscar", label: "Explorar" },
  { to: "/ranking", label: "Ranking" },
  { to: "/categorias", label: "Categorias" },
  { to: "/precos", label: "Preços" },
];

export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, loading, signOutUser } = useAuth();
  return (
    <div className="min-h-screen overflow-x-clip bg-paper">
      <a className={skipLinkClass} href="#conteudo">Pular para o conteúdo</a>
      <header className="relative z-50 h-[78px] border-b border-[rgba(64,43,69,.12)] bg-[rgba(248,245,249,.92)] shadow-[0_8px_30px_rgba(39,22,44,.04)] backdrop-blur-[14px]">
        <div className={`${shellClass} flex h-full items-center justify-between gap-6`}>
          <Brand />
          <nav className="hidden items-center gap-[27px] min-[820px]:flex" aria-label="Navegação principal">
            {publicLinks.map((item) => <NavLink className={({ isActive }) => classNames("relative text-[13px] font-bold text-[#625b67] hover:text-ink", isActive && "text-ink after:absolute after:right-0 after:-bottom-[27px] after:left-0 after:h-[3px] after:bg-forest after:content-['']")} key={item.to} to={item.to}>{item.label}</NavLink>)}
          </nav>
          <div className="hidden min-w-[184px] items-center justify-end gap-[7px] min-[820px]:flex">
            {loading
              ? <span className="size-[42px] animate-[auth-shimmer_1.1s_ease-in-out_infinite] rounded-full bg-[linear-gradient(90deg,#e5dce9_25%,#f4eef6_50%,#e5dce9_75%)] bg-[length:200%_100%]" role="status" aria-label="Validando sessão" />
              : user
                ? <AccountMenu user={user} onSignOut={signOutUser} />
                : <><Link to="/login" className={buttonClass("ghost", "sm")}>Entrar</Link><Link to="/cadastro" className={buttonClass("dark", "sm")}>Cadastrar</Link></>}
          </div>
          <button className="grid size-10 place-items-center border-0 bg-transparent text-inherit min-[820px]:hidden" onClick={() => setMenuOpen((open) => !open)} aria-label={menuOpen ? "Fechar menu" : "Abrir menu"} aria-expanded={menuOpen}>{menuOpen ? <X /> : <Menu />}</button>
        </div>
        {menuOpen && (
          <nav className="absolute top-[77px] right-0 left-0 grid gap-[3px] bg-paper px-5 pt-4 pb-6 shadow-card" aria-label="Navegação móvel">
            {publicLinks.map((item) => <NavLink className={mobileNavItemClass} key={item.to} to={item.to} onClick={() => setMenuOpen(false)}>{item.label}<ChevronRight size={18} /></NavLink>)}
            {loading
              ? <span className="min-h-12 px-1 py-[13px] text-[13px] text-muted" role="status">Validando sessão…</span>
              : user
                ? <>
                    <Link className={mobileNavItemClass} to="/dashboard" onClick={() => setMenuOpen(false)}>Meu painel<Gauge size={18} /></Link>
                    {user.role === "ADMIN" && <Link className={mobileNavItemClass} to="/admin" onClick={() => setMenuOpen(false)}>Painel administrativo<ShieldCheck size={18} /></Link>}
                    <button className={`${mobileNavItemClass} text-danger`} type="button" onClick={() => { setMenuOpen(false); void signOutUser(); }}>Sair<LogOut size={18} /></button>
                  </>
                : <>
                    <Link className={mobileNavItemClass} to="/login" onClick={() => setMenuOpen(false)}>Entrar<ChevronRight size={18} /></Link>
                    <Link className={mobileNavItemClass} to="/cadastro" onClick={() => setMenuOpen(false)}>Cadastrar<ChevronRight size={18} /></Link>
                  </>}
          </nav>
        )}
      </header>
      <DemoModeBanner />
      <main id="conteudo"><Outlet /></main>
      <Footer />
      <CookieConsent />
    </div>
  );
}

function Footer() {
  const columnClass = "flex flex-col items-start gap-[11px]";
  const headingClass = "mb-[5px] text-sm font-bold tracking-[.1em] text-lime uppercase";
  const linkClass = "text-xs text-[#aaa2ad] hover:text-white";
  return (
    <footer className="bg-[#120e15] pt-[70px] pb-[25px] text-white">
      <div className={`${shellClass} grid grid-cols-2 gap-x-[22px] gap-y-[38px] min-[820px]:grid-cols-[1.7fr_repeat(4,1fr)]`}>
        <div className={`${columnClass} col-span-full min-[820px]:col-span-1`}><Brand inverse /><p className="max-w-[310px] text-[13px] leading-[1.6] text-[#aaa2ad]">Uma vitrine mais clara para o talento brasileiro aparecer, crescer e ser escolhido.</p></div>
        <div className={columnClass}><strong className={headingClass}>Descubra</strong><Link className={linkClass} to="/buscar">Explorar criadores</Link><Link className={linkClass} to="/ranking">Ranking</Link><Link className={linkClass} to="/categorias">Categorias</Link></div>
        <div className={columnClass}><strong className={headingClass}>Score Creator</strong><Link className={linkClass} to="/sobre">Sobre</Link><Link className={linkClass} to="/precos">Planos</Link><Link className={linkClass} to="/contato">Fale com a gente</Link></div>
        <div className={columnClass}><strong className={headingClass}>Ajuda</strong><Link className={linkClass} to="/como-funciona">Como funciona</Link><Link className={linkClass} to="/faq">Perguntas frequentes</Link><Link className={linkClass} to="/diretrizes">Diretrizes</Link></div>
        <div className={columnClass}><strong className={headingClass}>Confiança</strong><Link className={linkClass} to="/termos">Termos de uso</Link><Link className={linkClass} to="/privacidade">Privacidade</Link></div>
      </div>
      <div className={`${shellClass} mt-[50px] flex flex-col gap-2 border-t border-[rgba(255,255,255,.14)] pt-5 text-[13px] text-[#aaa2ad] min-[820px]:flex-row min-[820px]:justify-between`}><span>© {new Date().getFullYear()} Score Creator</span><span>Feito para quem cria no Brasil.</span></div>
    </footer>
  );
}

const dashboardLinks = [
  { to: "/dashboard", label: "Visão geral", icon: Home, end: true },
  { to: "/dashboard/perfil", label: "Meu perfil", icon: UserRound },
  { to: "/dashboard/links", label: "Meus links", icon: Link2 },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/score", label: "Creator Score", icon: Gauge },
  { to: "/dashboard/destaques", label: "Destaques", icon: Star },
  { to: "/dashboard/assinatura", label: "Assinatura", icon: CircleDollarSign },
  { to: "/dashboard/configuracoes", label: "Configurações", icon: Settings },
];

export function ProtectedRoute({ admin = false }: { admin?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="grid min-h-screen place-content-center justify-items-center gap-3 bg-paper"><span className="size-[38px] animate-spin rounded-xl bg-ink shadow-[inset_0_0_0_9px_var(--lime)]" /> <p className="text-muted">Preparando seu espaço…</p></div>;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  if (!user.emailVerified && !user.isDevelopment) return <Navigate to="/verificar-email" replace />;
  if (admin && user.role !== "ADMIN") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

const dashboardShellClass = "dashboard-shell min-h-screen bg-cream max-[559px]:w-full max-[559px]:min-w-0 max-[559px]:overflow-x-clip";
const dashboardSidebarClass = "dashboard-sidebar fixed inset-y-0 left-0 z-[100] flex w-[min(286px,calc(100vw_-_48px))] -translate-x-full flex-col overflow-y-auto overscroll-contain bg-ink px-4 pt-[22px] pb-[max(22px,env(safe-area-inset-bottom))] text-white transition-transform duration-200 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden min-[820px]:w-[270px] min-[820px]:translate-x-0";
const dashboardNavClass = "grid shrink-0 gap-[3px]";
const dashboardNavItemClass = "flex min-h-[42px] items-center gap-2.5 rounded-[10px] px-[11px] text-[13px] font-bold text-[#9e96a2] hover:bg-[rgba(255,255,255,.05)] hover:text-white";
const dashboardNavActiveClass = "bg-[rgba(216,183,242,.14)] text-white shadow-[inset_3px_0_#b77bd2]";
const dashboardHeaderClass = "dashboard-header sticky top-0 z-50 flex h-[65px] items-center justify-between border-b border-line bg-[rgba(255,255,255,.9)] px-[17px] backdrop-blur-xl min-[820px]:px-7";
const dashboardContentClass = "dashboard-content min-w-0 px-4 pt-6 pb-[50px] max-[559px]:w-full max-[559px]:max-w-full max-[559px]:px-3 max-[559px]:pt-5 max-[559px]:pb-11 max-[559px]:[&_.card]:min-w-0 max-[559px]:[&_.card]:max-w-full max-[559px]:[&_form]:min-w-0 max-[559px]:[&_form]:max-w-full max-[559px]:[&_input]:min-w-0 max-[559px]:[&_input]:max-w-full max-[559px]:[&_select]:min-w-0 max-[559px]:[&_select]:max-w-full max-[559px]:[&_textarea]:min-w-0 max-[559px]:[&_textarea]:max-w-full max-[559px]:[&_.button]:max-w-full max-[559px]:[&_.button]:whitespace-normal max-[559px]:[&_.button]:text-center max-[559px]:[&_.button]:leading-[1.25] min-[820px]:px-7 min-[820px]:pt-[34px] min-[820px]:pb-[70px]";
const dashboardMenuButtonClass = "grid size-11 min-h-11 min-w-11 place-items-center border-0 bg-transparent text-inherit min-[820px]:hidden";
const dashboardPathClass = "hidden items-center gap-1.5 text-[13px] text-muted min-[820px]:flex [&_strong]:text-ink";
const sidebarBottomClass = "mt-auto grid gap-0.5 pt-3.5";
const sidebarLogoutClass = "flex items-center gap-2 border-0 bg-transparent p-2.5 text-[13px] text-[#8f8793] hover:text-white";

/*
 * Marks the document while an app shell is mounted so styles.css can hide the
 * window scrollbar there. It has to sit on <html>, which no component renders,
 * hence the attribute rather than a class on the shell div. Reference counted:
 * navigating between the dashboard and the admin panel mounts the next layout
 * before unmounting the previous one, and a plain remove would clear the flag
 * that the incoming shell just set.
 */
let appShellMounts = 0;
function useAppShellChrome() {
  useEffect(() => {
    appShellMounts += 1;
    document.documentElement.setAttribute('data-app-shell', '');
    return () => {
      appShellMounts -= 1;
      if (appShellMounts <= 0) document.documentElement.removeAttribute('data-app-shell');
    };
  }, []);
}

function DashboardUserChip({ name, email }: { name?: string; email?: string }) {
  return <div className="flex min-w-0 items-center gap-2"><span className="grid size-[34px] place-items-center rounded-full bg-lime font-black text-[#2b1632]">{name?.charAt(0)}</span><div className="hidden min-w-0 max-w-[min(280px,34vw)] flex-col min-[820px]:flex"><strong className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px]">{name}</strong><small className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted">{email}</small></div></div>;
}

export function DashboardLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { user, signOutUser } = useAuth();
  const entitlementsQuery = useQuery({ queryKey: queryKeys.entitlements, queryFn: api.entitlements, enabled: Boolean(user) });
  useAppShellChrome();

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeFromKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeFromKeyboard);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeFromKeyboard);
    };
  }, [menuOpen]);

  return (
    <div className={dashboardShellClass}>
      <a className={skipLinkClass} href="#dashboard-content">Pular para o conteúdo</a>
      <aside className={classNames(dashboardSidebarClass, menuOpen && "dashboard-sidebar--open translate-x-0")}>
        <div className="flex items-center justify-between px-[7px] pb-[25px]"><Brand inverse /><button className="border-0 bg-transparent text-white min-[820px]:hidden" onClick={() => setMenuOpen(false)} aria-label="Fechar menu"><X /></button></div>
        <nav className={dashboardNavClass} aria-label="Menu do painel">
          {dashboardLinks.map(({ to, label, icon: Icon, end }) => <NavLink className={({ isActive }) => classNames(dashboardNavItemClass, isActive && dashboardNavActiveClass)} key={to} to={to} end={end} onClick={() => setMenuOpen(false)}><Icon size={18} aria-hidden="true" />{label}</NavLink>)}
          {user?.role === "ADMIN" && <NavLink className={({ isActive }) => classNames(dashboardNavItemClass, isActive && dashboardNavActiveClass)} to="/admin" onClick={() => setMenuOpen(false)}><ShieldCheck size={18} aria-hidden="true" />Painel administrativo</NavLink>}
        </nav>
        <div className={sidebarBottomClass}>
          {entitlementsQuery.isSuccess && entitlementsQuery.data.plan === "FREE" && <div className="mb-3 rounded-xl border border-[rgba(255,255,255,.14)] bg-[rgba(216,183,242,.08)] p-3.5"><Sparkles className="text-lime" size={19} /><strong className="my-2 block text-xs">Faça seu nome circular</strong><p className="text-[13px] leading-normal text-[#9e96a2]">Libere analytics, links e personalização.</p><Link className="text-[13px] font-black text-lime" to="/dashboard/assinatura">Conhecer Premium</Link></div>}
          <button className={sidebarLogoutClass} onClick={() => void signOutUser()}><LogOut size={18} /> Sair</button>
        </div>
      </aside>
      {menuOpen && <button className="fixed inset-0 z-[90] border-0 bg-[rgba(24,13,28,.55)] min-[820px]:hidden" aria-label="Fechar menu" onClick={() => setMenuOpen(false)} />}
      <div className="min-w-0 min-[820px]:ml-[270px]">
        <header className={dashboardHeaderClass}>
          <button className={dashboardMenuButtonClass} onClick={() => setMenuOpen(true)} aria-label="Abrir menu"><Menu /></button>
          <div className={dashboardPathClass}><span>Painel</span><ChevronRight size={14} /><strong>{dashboardLinks.find((item) => item.to === location.pathname)?.label || "Score Creator"}</strong></div>
          <div className="flex min-w-0 items-center gap-[9px]"><DashboardUserChip name={user?.name} email={user?.email} /></div>
        </header>
        <DemoModeBanner />
        <main id="dashboard-content" className={dashboardContentClass}><Outlet /></main>
      </div>
    </div>
  );
}

const adminLinks = [
  { to: "/admin", label: "Resumo", icon: Layers3, end: true },
  { to: "/admin/usuarios", label: "Usuários", icon: Users },
  { to: "/admin/perfis", label: "Perfis", icon: Users },
  { to: "/admin/denuncias", label: "Denúncias", icon: ShieldCheck },
  { to: "/admin/categorias", label: "Categorias", icon: Compass },
  { to: "/admin/precos", label: "Preços", icon: CircleDollarSign },
  { to: "/admin/destaques", label: "Destaques", icon: Star },
  { to: "/admin/pagamentos", label: "Pagamentos", icon: BarChart3 },
  { to: "/admin/api", label: "API", icon: KeyRound },
  { to: "/admin/jobs", label: "Operações", icon: FileText },
];

export function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { user, signOutUser } = useAuth();
  useAppShellChrome();
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const current = adminLinks.find((item) => item.end ? item.to === location.pathname : location.pathname.startsWith(item.to));

  // Deliberately the dashboard's shell classes rather than a parallel set: the
  // drawer, the header and the chip then behave identically by construction
  // instead of being kept in step by hand.
  return (
    <div className={dashboardShellClass}>
      <a className={skipLinkClass} href="#admin-content">Pular para o conteúdo</a>
      <aside className={classNames(dashboardSidebarClass, menuOpen && "dashboard-sidebar--open translate-x-0")}>
        <div className="flex items-center justify-between px-[7px] pb-[25px]"><Brand inverse /><button className="border-0 bg-transparent text-white min-[820px]:hidden" onClick={() => setMenuOpen(false)} aria-label="Fechar menu"><X /></button></div>
        <span className="mx-[11px] mb-2.5 text-[11px] tracking-[.15em] text-[#9e96a2] uppercase">Administração</span>
        <nav className={dashboardNavClass} aria-label="Menu administrativo">
          {adminLinks.map(({ to, label, icon: Icon, end }) => <NavLink className={({ isActive }) => classNames(dashboardNavItemClass, isActive && dashboardNavActiveClass)} key={to} to={to} end={end} onClick={() => setMenuOpen(false)}><Icon size={18} aria-hidden="true" />{label}</NavLink>)}
        </nav>
        <div className={sidebarBottomClass}>
          <Link className={sidebarLogoutClass} to="/dashboard" onClick={() => setMenuOpen(false)}><ExternalLink size={18} aria-hidden="true" /> Ver painel</Link>
        </div>
        <button className={sidebarLogoutClass} onClick={() => void signOutUser()}><LogOut size={18} /> Sair</button>
      </aside>
      {menuOpen && <button className="fixed inset-0 z-[90] border-0 bg-[rgba(24,13,28,.55)] min-[820px]:hidden" aria-label="Fechar menu" onClick={() => setMenuOpen(false)} />}
      <div className="min-w-0 min-[820px]:ml-[270px]">
        <header className={dashboardHeaderClass}>
          <button className={dashboardMenuButtonClass} onClick={() => setMenuOpen(true)} aria-label="Abrir menu"><Menu /></button>
          <div className={dashboardPathClass}><span>Administração</span><ChevronRight size={14} /><strong>{current?.label || "Resumo"}</strong></div>
          <div className="flex min-w-0 items-center gap-[9px]"><DashboardUserChip name={user?.name} email={user?.email} /></div>
        </header>
        <DemoModeBanner />
        <main id="admin-content" className={dashboardContentClass}><Outlet /></main>
      </div>
    </div>
  );
}
